import React, { createContext, useContext, useMemo } from 'react';
import { GALLERY_CONFIG } from '../config/galleryConfig';
import { CATALOG } from '../config/catalog';
import {
    buildNavTree,
    findSectionByPath,
    membershipByPath,
    getArtwork as findArtwork,
    breadcrumbFor,
    primarySectionPath,
} from '../util/galleryResolver';
import { NavNode } from '../types/NavNode';
import { GallerySection } from '../types/GallerySection';
import { Artwork } from '../types/Artwork';
import { ViewingRoomMode } from '../types/ViewingRoomMode';
import { LandingConfig } from '../types/LandingConfig';

// Supplies the resolved gallery data + config-derived lookups to the whole app so
// components read it via a hook instead of prop drilling.
// Navigation *position* comes from the URL (useParams / GalleryRouter), not from here —
// this context holds data + config, never the current route.
export interface GalleryContextValue {
    navTree: NavNode[];
    viewingRoom: ViewingRoomMode;
    showCounts: boolean;
    defaultRoute: string;
    landing: LandingConfig;
    getSection: (slugPath: string[]) => GallerySection | undefined;
    getMembership: (slugPath: string[]) => Artwork[];
    getArtwork: (slug: string) => Artwork | undefined;
    getBreadcrumb: (slugPath: string[]) => string[];
    /** Primary section of an artwork (for the viewing-room SEO canonical). */
    getPrimarySectionPath: (slug: string) => string[] | undefined;
}

const GalleryContext = createContext<GalleryContextValue | null>(null);

export const GalleryProvider = ({ children }: { children: React.ReactNode }) => {
    // GALLERY_CONFIG + CATALOG are static module data, so this derives exactly once.
    const value = useMemo<GalleryContextValue>(
        () => ({
            navTree: buildNavTree(GALLERY_CONFIG, CATALOG),
            viewingRoom: GALLERY_CONFIG.viewingRoom,
            showCounts: GALLERY_CONFIG.showCounts,
            defaultRoute: GALLERY_CONFIG.defaultRoute,
            landing: GALLERY_CONFIG.landing,
            getSection: (slugPath) => findSectionByPath(GALLERY_CONFIG, slugPath),
            getMembership: (slugPath) => membershipByPath(GALLERY_CONFIG, CATALOG, slugPath),
            getArtwork: (slug) => findArtwork(slug, CATALOG),
            getBreadcrumb: (slugPath) => breadcrumbFor(GALLERY_CONFIG, slugPath),
            getPrimarySectionPath: (slug) => primarySectionPath(GALLERY_CONFIG, CATALOG, slug),
        }),
        [],
    );

    return <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>;
};

export const useGallery = (): GalleryContextValue => {
    const ctx = useContext(GalleryContext);
    if (!ctx) throw new Error('useGallery must be used within a GalleryProvider');
    return ctx;
};
